import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import PasswordRecoveryPage from "./pages/PasswordRecoveryPage";
import DashboardPage from "./pages/DashboardPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ResponseLibraryPage from "./pages/ResponseLibraryPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import SeapSearchPage from "./pages/SeapSearchPage";
import LessonsLearnedPage from "./pages/LessonsLearnedPage";
import CompetitorIntelligencePage from "./pages/CompetitorIntelligencePage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import SeapRadarPage from "./pages/SeapRadarPage";
import ReportsPage from "./pages/ReportsPage";
import EscalationPage from "./pages/EscalationPage";
import ComparatorPage from "./pages/ComparatorPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import ErpLayout from './components/erp/ErpLayout';
import OrganizationPage from './pages/erp/OrganizationPage';
import CatalogsPage from './pages/erp/CatalogsPage';
import RolesPage from './pages/erp/RolesPage';
import ModulesPage from './pages/erp/ModulesPage';
import DocumentsPage from './pages/erp/DocumentsPage';
import AuditPage from './pages/erp/AuditPage';
import AssetsPage from './pages/erp/AssetsPage';
import ErpProjectsPage from './pages/erp/ProjectsPage';
import PeoplePage from './pages/erp/PeoplePage';
import AllocationPage from './pages/erp/AllocationPage';
import CompliancePage from './pages/erp/CompliancePage';
import ExtractionPage from './pages/erp/ExtractionPage';
import WorkflowPage from './pages/erp/WorkflowPage';
import DiaryPage from './pages/erp/DiaryPage';
import InvoicingPage from './pages/erp/InvoicingPage';
import DashboardsPage from './pages/erp/DashboardsPage';
import MaterialsPage from './pages/erp/MaterialsPage';
import BudgetsPage from './pages/erp/BudgetsPage';
import NotificationsPage from './pages/erp/NotificationsPage';
import CopilotPage from './pages/erp/CopilotPage';
import RiskPage from './pages/erp/RiskPage';
import MobilePage from './pages/erp/MobilePage';
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Se incarca...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  return children;
};

function App() {
  useEffect(() => {
    const APP_TITLE = 'SAGA Infrastructura';
    document.title = APP_TITLE;
    // emergent-main.js may overwrite the title after load; keep it enforced.
    const observer = new MutationObserver(() => {
      if (document.title !== APP_TITLE) document.title = APP_TITLE;
    });
    const titleEl = document.querySelector('title');
    if (titleEl) observer.observe(titleEl, { childList: true });
    const t = setInterval(() => { if (document.title !== APP_TITLE) document.title = APP_TITLE; }, 1000);
    return () => { observer.disconnect(); clearInterval(t); };
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<PasswordRecoveryPage />} />
          <Route path="/reset-password" element={<PasswordRecoveryPage reset />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/:id" element={<ProjectDetailPage />} />
            <Route path="seap" element={<SeapSearchPage />} />
            <Route path="lessons" element={<LessonsLearnedPage />} />
            <Route path="competitors" element={<CompetitorIntelligencePage />} />
            <Route path="knowledge" element={<KnowledgeBasePage />} />
            <Route path="seap-radar" element={<SeapRadarPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="escalation" element={<EscalationPage />} />
            <Route path="comparator" element={<ComparatorPage />} />
            <Route path="admin/users" element={<AdminUsersPage />} />
            <Route path="library" element={<ResponseLibraryPage />} />
            <Route path="company" element={<CompanyProfilePage />} />
            <Route path="erp" element={<ErpLayout />}>
              <Route path="dashboards" element={<DashboardsPage />} />
              <Route path="organization" element={<OrganizationPage />} />
              <Route path="catalogs" element={<CatalogsPage />} />
              <Route path="projects" element={<ErpProjectsPage />} />
              <Route path="extraction" element={<ExtractionPage />} />
              <Route path="workflows" element={<WorkflowPage />} />
              <Route path="diary" element={<DiaryPage />} />
              <Route path="invoicing" element={<InvoicingPage />} />
              <Route path="materials" element={<MaterialsPage />} />
              <Route path="budgets" element={<BudgetsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="copilot" element={<CopilotPage />} />
              <Route path="risk" element={<RiskPage />} />
              <Route path="mobile" element={<MobilePage />} />
              <Route path="fleet" element={<AssetsPage moduleKey="fleet" />} />
              <Route path="equipment" element={<AssetsPage moduleKey="equipment" />} />
              <Route path="people" element={<PeoplePage />} />
              <Route path="allocation" element={<AllocationPage />} />
              <Route path="compliance" element={<CompliancePage />} />
              <Route path="roles" element={<RolesPage />} />
              <Route path="modules" element={<ModulesPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="audit" element={<AuditPage />} />
            </Route>
          </Route>
        </Routes>
        <Toaster position="top-right" theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
